import React, { useState, useMemo } from 'react'
import {
  HiEye,
  HiCalendarDays,
  HiUser,
  //HiClock,
  HiExclamationTriangle,
  //HiChevronRight,
  HiFunnel,
  HiMagnifyingGlass,
} from 'react-icons/hi2'
import { Badge, Input, UserAvatar } from '../../../../components'
import { formatDateSimple, extractPlainText } from '@/utils'
import type { Demanda } from '../../../../models'
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getPriorityColor,
  getPriorityIcon,
} from '../../utils/demandUtils'

interface SideDemandTableProps {
  demandas: Demanda[]
  selectedDemandId?: number | undefined
  onSelectDemand?: ((demanda: Demanda) => void) | undefined
  onViewDemand?: ((demanda: Demanda) => void) | undefined
  isLoading?: boolean
  className?: string
  maxHeight?: string
  showSearch?: boolean
  showFilter?: boolean
  title?: string
}

export const SideDemandTable: React.FC<SideDemandTableProps> = ({
  demandas,
  selectedDemandId,
  onSelectDemand,
  onViewDemand,
  isLoading = false,
  className = '',
  maxHeight = 'calc(100vh - 333px)',
  showSearch = true,
  showFilter = false,
  title = 'Demandas Relacionadas',
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Filtrar demandas baseado na busca e filtros
  const filteredDemandas = useMemo(() => {
    let filtered = demandas

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(
        demanda =>
          demanda.titulo.toLowerCase().includes(searchLower) ||
          extractPlainText(demanda.descricao || '').toLowerCase().includes(searchLower) ||
          demanda.demandante?.name.toLowerCase().includes(searchLower) ||
          demanda.processo?.nome_processo.toLowerCase().includes(searchLower)
      )
    }

    if (statusFilter) {
      filtered = filtered.filter(demanda => demanda.status === statusFilter)
    }

    return filtered
  }, [demandas, searchTerm, statusFilter])

  const handleDemandClick = (demanda: Demanda) => {
    onSelectDemand?.(demanda)
  }

  const handleViewClick = (e: React.MouseEvent, demanda: Demanda) => {
    e.stopPropagation()
    onViewDemand?.(demanda)
  }

  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 ${className}`} style={{ maxHeight }}>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-5/6"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 flex flex-col ${className}`} style={{ maxHeight }}>
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredDemandas.length} de {demandas.length}
          </span>
        </div>

        {/* Search and Filter */}
        {(showSearch || showFilter) && (
          <div className="space-y-2">
            {showSearch && (
              <div className="relative">
                <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar demandas..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
            )}

            {showFilter && (
              <div className="relative">
                <HiFunnel className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  title="Filtrar por status"
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todos os status</option>
                  <option value="EMATRIBUICAO">Em Atribuição</option>
                  <option value="ATRIBUIDA">Atribuída</option>
                  <option value="EMPROGRESSO">Em Progresso</option>
                  <option value="AGUARDANDOAUTORIZACAO">Aguardando Autorização</option>
                  <option value="SOLUCIONADA">Solucionada</option>
                  <option value="FINALIZADA">Finalizada</option>
                  <option value="DEVOLVIDA">Devolvida</option>
                  <option value="ERRADA">Errada</option>
                  <option value="RECUSADA">Recusada</option>
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista de Demandas */}
      <div
        className="flex-1 overflow-y-auto min-h-0 p-3"
      >
        {filteredDemandas.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400">
            <HiExclamationTriangle className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-center">
              {searchTerm || statusFilter ? 'Nenhuma demanda encontrada com os filtros aplicados' : 'Nenhuma demanda encontrada'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDemandas.map((demanda) => {
              const isSelected = selectedDemandId === demanda.id
              const descricaoLimpa = extractPlainText(demanda.descricao || '')
              const descricaoPreview = descricaoLimpa.length > 80
                ? `${descricaoLimpa.substring(0, 80)}...`
                : descricaoLimpa

              return (
                <div
                  key={demanda.id}
                  onClick={() => handleDemandClick(demanda)}
                  className={`
                    p-4 cursor-pointer transition-all duration-200 group rounded-lg border
                    ${isSelected 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 shadow-md ring-2 ring-blue-500/20' 
                      : 'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-md hover:bg-gray-50 dark:hover:bg-neutral-700/50'
                    }
                  `}
                >
                  {/* Header da Demanda */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
                        #{demanda.id}
                      </span>
                      <Badge
                        variant={getStatusColor(demanda.status || '')}
                        size="sm"
                        className="flex items-center space-x-1 flex-shrink-0"
                      >
                        {React.createElement(getStatusIcon(demanda.status || ''), {
                          className: 'w-2.5 h-2.5',
                        })}
                        <span className="text-xs">{getStatusLabel(demanda.status || '')}</span>
                      </Badge>
                    </div>
                    
                    {/* Ação de visualizar */}
                    <button
                      onClick={(e) => handleViewClick(e, demanda)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-200 dark:hover:bg-neutral-600"
                      title="Visualizar demanda"
                    >
                      <HiEye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>

                  {/* Título */}
                  <h4 className={`text-sm font-medium mb-2 line-clamp-2 ${
                    isSelected 
                      ? 'text-blue-900 dark:text-blue-100' 
                      : 'text-gray-900 dark:text-gray-100'
                  }`}>
                    {demanda.titulo}
                  </h4>

                  {/* Prioridade e Prazo */}
                  <div className="flex items-center justify-between mb-2">
                    <Badge
                      variant={getPriorityColor(demanda.prioridade)}
                      size="sm"
                      className="flex items-center space-x-1"
                    >
                      {React.createElement(getPriorityIcon(demanda.prioridade), {
                        className: 'w-2.5 h-2.5',
                      })}
                      <span className="text-xs">{demanda.prioridade}</span>
                    </Badge>

                    {demanda.sugestao_entrega && (
                      <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                        <HiCalendarDays className="w-3 h-3" />
                        <span>{formatDateSimple(demanda.sugestao_entrega)}</span>
                      </div>
                    )}
                  </div>

                  {/* Descrição Preview */}
                  {descricaoPreview && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                      {descricaoPreview}
                    </p>
                  )}

                  {/* Footer - Demandante e Processo */}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                      {demanda.demandante ? (
                        <>
                          <UserAvatar
                            user={demanda.demandante}
                            size="xs"
                            showName={false}
                            showPopup={false}
                          />
                          <span className="truncate">{demanda.demandante.name}</span>
                        </>
                      ) : (
                        <>
                          <HiUser className="w-3 h-3" />
                          <span>Não informado</span>
                        </>
                      )}
                    </div>

                    {/* {demanda.processo && (
                      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                        <span className="truncate max-w-20" title={demanda.processo.nome_processo}>
                          {demanda.processo.nome_processo}
                        </span>
                      </div>
                    )} */}
                  </div>

                  {/* Data de criação */}
                  {/* <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-neutral-700">
                    <div className="flex items-center space-x-1 text-xs text-gray-400 dark:text-gray-500">
                      <HiClock className="w-3 h-3" />
                      <span>{formatDateSimple(demanda.created_at)}</span>
                    </div>

                    {isSelected && (
                      <HiChevronRight className="w-4 h-4 text-blue-500" />
                    )}
                  </div> */}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default SideDemandTable
